import React, { useMemo } from "react";
import { Link } from "react-router-dom";

import classNames from "classnames";
import { Table } from "react-bootstrap";

import { useEjectionReportsContext } from "../contexts/EjectionReports";
import { dateFormat } from "../lib/dateFormat";
import { SortColumns } from "../types/games";

import { usePaginationContext } from "../../../../contexts/Pagination";
import { useAppSelector } from "../../../../hooks/reduxHooks";
import { selectAllSites } from "../../../../features/global/globalSlice";
import { getNameOfEjected } from "../lib/getNameOfEjected";

const EjectionReportsTable = React.memo(() => {
    const sites = useAppSelector(selectAllSites);
    const { reports, sortColumn, sortOrder, setSortColumn, setSortOrder } =
        useEjectionReportsContext();
    const { page, itemsPerPage: reportsPerPage } = usePaginationContext();

    const paginatedReports = useMemo(() => {
        return reports.slice(
            page * reportsPerPage - reportsPerPage,
            page * reportsPerPage,
        );
    }, [reports, page, reportsPerPage]);

    const handleHeaderClick = (column: SortColumns) => {
        if (column !== sortColumn) {
            setSortColumn(column);
            setSortOrder("ascending");
        } else {
            setSortOrder(prev =>
                prev === "ascending" ? "descending" : "ascending",
            );
        }
    };

    return (
        <Table striped className={classNames("dataTable")}>
            <thead>
                <tr>
                    <th
                        onClick={() => handleHeaderClick("date")}
                        className={classNames({
                            sorting_asc:
                                sortColumn === "date" &&
                                sortOrder === "ascending",
                            sorting_desc:
                                sortColumn === "date" &&
                                sortOrder === "descending",
                        })}
                    >
                        Date
                    </th>
                    <th
                        onClick={() => handleHeaderClick("site")}
                        className={classNames({
                            sorting_asc:
                                sortColumn === "site" &&
                                sortOrder === "ascending",
                            sorting_desc:
                                sortColumn === "site" &&
                                sortOrder === "descending",
                        })}
                    >
                        Site
                    </th>
                    <th data-sortable="false">Game</th>
                    <th data-sortable="false">
                        Name of Ejected Person (if known)
                    </th>
                    <th
                        onClick={() => handleHeaderClick("umpire")}
                        className={classNames({
                            sorting_asc:
                                sortColumn === "umpire" &&
                                sortOrder === "ascending",
                            sorting_desc:
                                sortColumn === "umpire" &&
                                sortOrder === "descending",
                        })}
                    >
                        Umpire
                    </th>
                    <th data-sortable="false"></th>
                </tr>
            </thead>
            <tbody>
                {paginatedReports.map(r => {
                    const site = sites.find(s => s.id === r.site);
                    return (
                        <tr key={r.id}>
                            <td>{dateFormat.format(new Date(r.date))}</td>
                            <td>{site?.displayName}</td>
                            <td>
                                {`(${r.ageLevel.ageLevel}) ${r.visitor.name || "TBD"} @ ${r.home.name || "TBD"}`}
                            </td>
                            <td>{getNameOfEjected(r)}</td>
                            <td>
                                {r.umpires?.find(u => r.userId === u.id)?.name}
                            </td>
                            <td>
                                <Link to={`/admin/ejection-reports/${r.id}`}>
                                    View Report
                                </Link>
                            </td>
                        </tr>
                    );
                })}
            </tbody>
            <tfoot>
                <tr>
                    <th>Date</th>
                    <th>Site</th>
                    <th>Game</th>
                    <th>Name of Ejected Person (if known)</th>
                    <th>Umpire</th>
                    <th></th>
                </tr>
            </tfoot>
        </Table>
    );
});

export default EjectionReportsTable;
